// AI-assisted: Claude 2026-08-07, human-reviewed: ausstehend
// Geführtes Training: Warteschlange mit Skip (Gerät belegt → kommt wieder),
// Satz-Eingabe inline, Intervall-Timer, Abschluss-Fest mit Spruch + Dauer.
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import type { Exercise, ExerciseNote, Feel, Plan, PlanDay, PlanDayItem, SetEntry, WorkoutEntry } from "@/lib/types"
import {
  getAllEntries, getPlans, getPlanDays, getPlanDayItems, getExercises, getExerciseNotes,
  saveEntry, onSynced, sofort, nachId, planTage, activePlanId,
} from "@/lib/data"
import { loadSession, storeSession, type TrainingSession } from "@/lib/session"
import { createQueue, current, skip, drop, complete, isFinished, progress } from "@/lib/workoutQueue"
import { cleanSets, volume, fmtSet, isBodyweight, repsSpalte } from "@/lib/sets"
import { prefillSets, lastEntryFor } from "@/lib/prefill"
import { milestoneCrossed } from "@/lib/stats"
import { todayISO, dayOf, fmtDate } from "@/lib/dates"
import { celebrate } from "@/lib/celebrate"
import { toast } from "@/lib/toast"
import { randomSpruch } from "@/lib/sprueche"
import { unlockAudio } from "@/lib/timer"
import { hatEigenesVideo, videoUrl } from "@/lib/video"
import { istEigene } from "@/lib/uebungen"
import { IconVideo } from "@/components/icons"
import UebungForm from "@/components/plans/UebungForm"
import VideoPanel from "@/components/VideoPanel"
import { currentTheme, istDunkel, themeTemporaer, trainingDunkel } from "@/lib/theme"
import { publishWorkout } from "@/lib/feed"
import { pb } from "@/lib/pb"
import SetList from "@/components/log/SetList"
import FeelPicker from "@/components/log/FeelPicker"
import IntervalTimer from "@/components/training/IntervalTimer"
import RestTimer from "@/components/training/RestTimer"
import Wichtel from "@/components/celebration/creatures/Wichtel"
import "@/styles/log.css"
import "@/styles/training.css"

type Session = TrainingSession

/** Name des Plans, nach dem gerade trainiert wird — dieselbe Wahl wie planTage(). */
function aktiverPlanName(plans: Plan[] | null): string {
  if (!plans) return ""
  const p = plans.find((x) => x.id === activePlanId()) ?? plans.find((x) => !x.owner) ?? plans[0]
  return p?.name ?? ""
}

export default function TrainingPage() {
  const navigate = useNavigate()

  // Option „Training immer dunkel": nur ANZEIGEN, gespeicherte Wahl bleibt —
  // beim Verlassen des Tabs kommt das eigene Theme zurück
  useEffect(() => {
    if (!trainingDunkel() || istDunkel(currentTheme())) return
    themeTemporaer("nacht")
    return () => themeTemporaer(null)
  }, [])

  // Sofort-Speicher: beim Tab-Wechsel steht die Seite schon (Runde 16)
  const [days, setDays] = useState<PlanDay[]>(() => planTage(sofort.plans(), sofort.planDays()))
  const [items, setItems] = useState<PlanDayItem[]>(() => sofort.planDayItems() ?? [])
  const [exercises, setExercises] = useState<Map<string, Exercise>>(() => nachId(sofort.exercises()))
  const [entries, setEntries] = useState<WorkoutEntry[]>(() => sofort.entries() ?? [])
  const [loaded, setLoaded] = useState(() => sofort.plans() !== null)
  const [planName, setPlanName] = useState(() => aktiverPlanName(sofort.plans()))
  const [session, setSessionState] = useState<Session | null>(null)
  const [doneInfo, setDoneInfo] = useState<{ spruch: string; minutes: number; count: number; vol: number } | null>(null)
  const [sets, setSets] = useState<SetEntry[]>([])
  const [doneSets, setDoneSets] = useState<boolean[]>([])
  const [restFor, setRestFor] = useState<number | null>(null) // Satz-Index, der die Pause gestartet hat
  const [pauseAfter, setPauseAfter] = useState<number | null>(null) // C6: Pause nach abgeschlossener Übung (s)
  const [confirmAbort, setConfirmAbort] = useState(false)
  const [schmerzFrage, setSchmerzFrage] = useState(false)
  const [feel, setFeel] = useState<Feel>("gut")
  const [saving, setSaving] = useState(false)
  const [ok, setOk] = useState(false) // Erfolgs-Morph-Phase (Skill §10)
  const [dx, setDx] = useState(0)
  const [offenerTag, setOffenerTag] = useState<string | null>(null)
  const [bearbeiten, setBearbeiten] = useState(false)
  const [notizen, setNotizen] = useState<ExerciseNote[]>([])
  const touchX = useRef<number | null>(null)

  const ladeNotizen = async () => setNotizen(await getExerciseNotes())
  const notizVon = (exId: string) => {
    const n = notizen.find((x) => x.exercise === exId)
    return { video_url: n?.video_url ?? "", note: n?.note ?? "" }
  }

  function morph() {
    setOk(true)
    window.setTimeout(() => setOk(false), 900)
  }

  async function setSession(next: Session | null) {
    setSessionState(next)
    await storeSession(next)
  }

  useEffect(() => {
    let alive = true
    const load = async () => {
      const [plans, allDays, allItems, exs, ents, nots] = await Promise.all([
        getPlans(), getPlanDays(), getPlanDayItems(), getExercises(), getAllEntries(), getExerciseNotes(),
      ])
      if (!alive) return
      setNotizen(nots)
      setPlanName(aktiverPlanName(plans))
      setDays(planTage(plans, allDays))
      setItems(allItems)
      setExercises(nachId(exs))
      setEntries(ents)
      setLoaded(true)
    }
    void load()
    void loadSession().then((s) => {
      if (alive && s) setSessionState(s)
    })
    const off = onSynced(() => { void load() })
    return () => { alive = false; off() }
  }, [])

  const itemsOfDay = (dayId: string) =>
    items.filter((i) => i.plan_day === dayId).sort((a, b) => a.sort - b.sort)

  const currentItem = useMemo(() => {
    if (!session) return null
    const id = current(session.queue)
    return id ? items.find((i) => i.id === id) ?? null : null
  }, [session, items])

  const currentExercise = currentItem ? exercises.get(currentItem.exercise) ?? null : null

  // Beim Übungswechsel: Sätze aus dem letzten Eintrag vorbelegen, Gefühl zurücksetzen
  useEffect(() => {
    if (!currentExercise) return
    const { sets: pre } = prefillSets(entries, currentExercise.id, currentItem?.target_sets ?? 0, isBodyweight(currentExercise.equipment))
    setSets(pre)
    setBearbeiten(false)
    setDoneSets([])
    setRestFor(null)
    setFeel("gut")
    setDx(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentExercise?.id])

  async function start(day: PlanDay) {
    const ids = itemsOfDay(day.id).map((i) => i.id)
    if (!ids.length) { toast("Dieser Tag hat noch keine Übungen"); return }
    unlockAudio() // Nutzer-Geste — schaltet die Pausen-Piepser auf iOS frei
    setDoneInfo(null)
    await setSession({
      dayId: day.id, dayCode: day.code, date: todayISO(),
      queue: createQueue(ids), startedAt: Date.now(), savedCount: 0, volumeSum: 0,
    })
  }

  async function doSkip() {
    if (!session) return
    setPauseAfter(null) // Wegwischen beendet auch die Übungspause
    await setSession({ ...session, queue: skip(session.queue) })
    toast("Kommt gleich wieder dran ↷")
  }

  /** Schmerzen: Übung heute ganz auslassen. Sie kommt nicht wieder — anders
   *  als beim belegten Gerät. Nichts wird gespeichert, also verfälscht die
   *  ausgelassene Übung auch keine Statistik (Thors Wunsch Runde 18). */
  async function doDrop() {
    if (!session) return
    setPauseAfter(null)
    setSchmerzFrage(false)
    await setSession({ ...session, queue: drop(session.queue) })
    toast("Heute ausgelassen. Gut auf dich aufgepasst.")
  }

  // Satz abhaken: ON und nicht der letzte Satz → Pause starten; OFF nimmt die eigene Pause zurück
  function toggleSetDone(i: number) {
    const on = !doneSets[i]
    const next = [...doneSets]
    next[i] = on
    setDoneSets(next)
    if (on && i < sets.length - 1) setRestFor(i)
    else if (!on && restFor === i) setRestFor(null)
  }

  async function completeCurrent() {
    if (!session || !currentItem || !currentExercise || saving) return
    const clean = cleanSets(sets)
    if (!clean.length) { toast("Mindestens ein Satz mit Wiederholungen"); return }

    setSaving(true)
    setRestFor(null) // Übung fertig — laufende Pause weg
    try {
      const ms = session.savedCount === 0 ? milestoneCrossed(entries, session.date) : null
      const saved = await saveEntry({
        date: session.date, plan_day: currentItem.plan_day, day_code: session.dayCode,
        exercise: currentExercise.id, feel, note: "", sets: clean,
      })
      setEntries((prev) => [...prev, saved])
      morph()
      celebrate({ kind: "save" })
      if (ms) { celebrate({ kind: "milestone", text: ms }); toast(ms) }

      const nextQueue = complete(session.queue)
      const next: Session = {
        ...session, queue: nextQueue,
        savedCount: session.savedCount + 1,
        volumeSum: session.volumeSum + volume(clean),
      }
      if (isFinished(nextQueue)) {
        const minutes = Math.max(1, Math.round((Date.now() - session.startedAt) / 60000))
        const spruch = randomSpruch()
        setDoneInfo({ spruch, minutes, count: next.savedCount, vol: next.volumeSum })
        // Feed ist Bonus — publishWorkout ist still, wenn Teilen aus ist, und wirft nie
        void publishWorkout({
          date: session.date, day_code: session.dayCode,
          exercises_count: next.savedCount, volume_kg: next.volumeSum, duration_min: minutes,
        })
        celebrate({ kind: "workout-complete", summary: spruch })
        await setSession(null)
      } else {
        await setSession(next)
        toast(`Gespeichert: ${currentExercise.name} ✓`)
        // C6: geplante Pause nach der Übung — Countdown über der nächsten Übung
        if (currentItem.pause_after_sec > 0) setPauseAfter(currentItem.pause_after_sec)
      }
    } finally {
      setSaving(false)
    }
  }

  // Kein window.confirm — der native Dialog wird in PWA/eingebetteten
  // Browsern teils stumm verschluckt (Bug-Report Thor). Inline-Zweischritt.
  async function abort() {
    if (!session) return
    await setSession(null)
    setConfirmAbort(false)
    toast("Training beendet – gespeicherte Übungen bleiben erhalten.")
  }

  // ── Swipe (Gerät belegt → weiter) ──
  function onTouchStart(e: React.TouchEvent) { touchX.current = e.touches[0].clientX }
  function onTouchMove(e: React.TouchEvent) {
    if (touchX.current === null) return
    setDx(e.touches[0].clientX - touchX.current)
  }
  function onTouchEnd() {
    if (Math.abs(dx) > 70) void doSkip()
    touchX.current = null
    setDx(0)
  }

  if (!loaded) return null

  // ── Abschluss-Fest ──
  if (doneInfo) {
    return (
      <div className="tr-done">
        <h2>Training geschafft</h2>
        <div className="card tr-done-card">
          <p className="tr-spruch">„{doneInfo.spruch}"</p>
          <div className="tr-stats">
            <div><b>{doneInfo.count}</b><span>Übungen</span></div>
            <div><b>{Math.round(doneInfo.vol).toLocaleString("de-DE")}</b><span>kg Volumen</span></div>
            <div><b>{doneInfo.minutes}</b><span>Minuten</span></div>
          </div>
          <button className="save" onClick={() => navigate("/verlauf")}>Verlauf ansehen</button>
          <button className="btn-secondary tr-again" onClick={() => setDoneInfo(null)}>Schließen</button>
        </div>
      </div>
    )
  }

  // ── Startbildschirm ──
  if (!session) {
    if (!days.length) {
      return (
        <div className="empty">
          <div className="empty-critter"><Wichtel /></div>
          <p><b>Kein Plan? Bau dir einen.</b></p>
          <button type="button" className="btn-secondary" onClick={() => navigate("/plaene")}>Plan anlegen</button>
        </div>
      )
    }
    return (
      <>
        {/* Welcher Plan gerade gilt — und ein Weg raus, ohne Suchen
            (Thors Feedback Runde 17) */}
        <div className="tr-planzeile">
          <span className="tr-planname">{planName || "Dein Plan"}</span>
          <button type="button" className="tr-planwechsel" onClick={() => navigate("/plaene")}>
            Anderer Plan ›
          </button>
        </div>
        <h2>Welcher Tag ist heute dran?</h2>
        {/* EINE Karte für alle Tage statt Karten-Stapel (Skill §4).
            Jeder Tag zeigt auf Wunsch seine Übungen (Thors Feedback Runde 11). */}
        <div className="card">
          {days.map((d) => {
            const dayItems = itemsOfDay(d.id)
            const offen = offenerTag === d.id
            return (
              <div className="tr-day-block" key={d.id}>
                <div className="tr-day">
                  <button
                    type="button"
                    className="tr-day-head"
                    aria-expanded={offen}
                    onClick={() => setOffenerTag(offen ? null : d.id)}
                  >
                    <b>{d.name || `Tag ${d.code}`}</b>
                    <span className="hint" style={{ margin: 0 }}>
                      {dayItems.length} Übungen · {offen ? "zuklappen" : "ansehen"}
                    </span>
                  </button>
                  <button className="btn-secondary" onClick={() => void start(d)}>Training starten</button>
                </div>
                {offen && (
                  <div className="tr-day-list">
                    {!dayItems.length && <p className="hint">Dieser Tag hat noch keine Übungen.</p>}
                    {dayItems.map((it) => {
                      const ex = exercises.get(it.exercise)
                      return (
                        <div className="tr-vorschau" key={it.id}>
                          <span className="plan-thumb" aria-hidden="true">
                            {ex?.media_slug ? (
                              <img src={`/media/exercises/${ex.media_slug}.jpg`} alt="" loading="lazy"
                                onError={(e) => { e.currentTarget.style.display = "none" }} />
                            ) : (
                              <span className="thumb-leer"><IconVideo size={18} /></span>
                            )}
                          </span>
                          <span className="tr-vorschau-name">{ex?.name ?? "Übung"}</span>
                          <span className="tr-vorschau-ziel">
                            {it.target_sets ? `${it.target_sets}×` : ""}{it.target_reps}
                          </span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <p className="hint">Die App führt dich Übung für Übung durch. Gerät belegt? Einfach weiterwischen – die Übung kommt später automatisch wieder dran.</p>
        <button type="button" className="btn-secondary pressable tr-planbtn" onClick={() => navigate("/plan")}>
          Diesen Plan ansehen oder ändern
        </button>
      </>
    )
  }

  // ── Aktive Übung ──
  const prog = progress(session.queue)
  const last = currentExercise ? lastEntryFor(entries, currentExercise.id) : null
  const restSec = (currentItem?.rest_sec
    || (pb.authStore.record?.default_rest_sec as number)
    || 90)

  return (
    <>
      <div className="tr-progress" aria-label={`Übung ${prog.done + 1} von ${prog.total}`}>
        <div className="tr-progress-bar"><div style={{ width: `${(prog.done / prog.total) * 100}%` }} /></div>
        <span>Übung {Math.min(prog.done + 1, prog.total)} von {prog.total}</span>
      </div>

      {currentExercise && currentItem && (
        <div
          className="tr-card"
          style={{ transform: dx ? `translateX(${dx}px) rotate(${dx / 40}deg)` : undefined, opacity: dx ? Math.max(0.5, 1 - Math.abs(dx) / 300) : undefined }}
          onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
        >
          {/* Bild zuerst und groß (Referenz-Muster: Übungen zeigen ihr Bild).
              Ohne Bild tritt der Video-Link an seine Stelle — nie eine leere Fläche. */}
          <div className="tr-head">
            {currentExercise.media_slug ? (
              <img
                src={`/media/exercises/${currentExercise.media_slug}.gif`} alt=""
                loading="lazy" className="tr-gif"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }}
              />
            ) : (
              <a className="tr-videobox" href={videoUrl(currentExercise)} target="_blank" rel="noreferrer">
                <IconVideo size={26} />
                <b>Video ansehen</b>
                <span>Kein Bild vorhanden – auf YouTube suchen ↗</span>
              </a>
            )}
            <h3>{currentExercise.name}</h3>
            <p className="tr-target">
              Ziel: {currentItem.target_sets ? `${currentItem.target_sets}×` : ""}{currentItem.target_reps}
              {last && <span className="tr-ghost"> · Letztes Mal: {last.sets.map(fmtSet).join(" · ")} ({fmtDate(dayOf(last.date))})</span>}
            </p>
          </div>

          {bearbeiten ? (
            <UebungForm
              exercise={currentExercise}
              notiz={notizVon(currentExercise.id)}
              onFertig={() => { setBearbeiten(false); void ladeNotizen() }}
              onAbbruch={() => setBearbeiten(false)}
            />
          ) : (
            <details className="tr-details">
              <summary>Ausführung ansehen</summary>
              {(currentExercise.execution_text || currentExercise.instructions) && (
                <p>{currentExercise.execution_text || currentExercise.instructions}</p>
              )}
              {hatEigenesVideo(currentExercise) ? (
                <VideoPanel url={currentExercise.video_url ?? ""} titel={currentExercise.name} />
              ) : (
                <a href={videoUrl(currentExercise)} target="_blank" rel="noreferrer">Video auf YouTube suchen ↗</a>
              )}
              <button type="button" className="tr-bearbeiten" onClick={() => setBearbeiten(true)}>
                {istEigene(currentExercise) ? "Übung bearbeiten" : "Video/Notiz hinterlegen"}
              </button>
            </details>
          )}

          {currentItem.timer && <IntervalTimer key={currentItem.id} config={currentItem.timer} />}

          {pauseAfter !== null && (
            <RestTimer
              key={`pause-${currentItem.id}`}
              seconds={pauseAfter}
              onDone={() => setPauseAfter(null)}
              onSkip={() => setPauseAfter(null)}
            />
          )}

          {restFor !== null && (
            <RestTimer
              key={restFor}
              seconds={restSec}
              onDone={() => setRestFor(null)}
              onSkip={() => setRestFor(null)}
            />
          )}

          <div className="tr-sets">
            <SetList key={currentExercise.id} sets={sets} onChange={setSets} doneFlags={doneSets} onToggleDone={toggleSetDone} hideWeight={isBodyweight(currentExercise.equipment)} repsLabel={repsSpalte(currentItem?.target_reps || currentExercise.default_reps)} />
          </div>

          <h2 style={{ marginTop: 14 }}>Gefühl</h2>
          <FeelPicker value={feel} onChange={setFeel} />
          {feel === "schmerz" && (
            <p className="hint">Bei Ausstrahlung, Kribbeln oder Taubheit ins Bein: Training abbrechen und ärztlich abklären.</p>
          )}

        </div>
      )}

      {currentExercise && currentItem && (
        <>
          {/* Aktionen außerhalb der Wisch-Fläche — der Abschluss klebt (Skill §13.5) */}
          <button className={`save save-sticky btn-success-morph${ok ? " ok" : ""}`} disabled={saving || ok} onClick={() => void completeCurrent()}>
            <span className="bsm-label">{saving ? "Speichert…" : "Übung abschließen ✓"}</span>
            <svg className="bsm-check" viewBox="0 0 24 24" aria-hidden="true">
              <path pathLength="1" d="M4.5 12.5 10 18 19.5 6.5" />
            </svg>
          </button>
          <button className="btn-secondary tr-skip" onClick={() => void doSkip()}>Gerät belegt – später ↷</button>

          {/* Schmerz-Ausstieg: bewusst zweistufig. Ein Fehltipper soll die
              Übung nicht für den ganzen Tag aus dem Plan nehmen. */}
          {schmerzFrage ? (
            <div className="tr-schmerz-frage">
              <p>Tut die Übung heute weh? Dann lassen wir sie aus – sie kommt heute nicht wieder.</p>
              <div>
                <button className="btn-secondary" onClick={() => setSchmerzFrage(false)}>Doch weitermachen</button>
                <button className="tr-schmerz-ja" onClick={() => void doDrop()}>Ja, auslassen</button>
              </div>
            </div>
          ) : (
            <button className="btn-secondary tr-schmerz" onClick={() => setSchmerzFrage(true)}>
              Schmerzen – Übung heute auslassen
            </button>
          )}
        </>
      )}

      {confirmAbort ? (
        <div className="tr-abort-confirm">
          <p>Training abbrechen? Gespeicherte Übungen bleiben erhalten.</p>
          <div>
            <button className="btn-secondary" onClick={() => setConfirmAbort(false)}>Weiter trainieren</button>
            <button className="tr-abort-yes" onClick={() => void abort()}>Ja, abbrechen</button>
          </div>
        </div>
      ) : (
        <button className="tr-abort" onClick={() => setConfirmAbort(true)}>Training abbrechen</button>
      )}
    </>
  )
}
